// src/components/files/TrashView.tsx
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  alpha,
} from '@mui/material';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreIcon from '@mui/icons-material/Restore';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useTranslation } from 'react-i18next';
import { useFileManager } from '../../contexts/FileManagerContext';
import { formatFileSize } from '../../services/fileService';
import EmptyState from './EmptyState';

const TrashView: React.FC = () => {
  const { t } = useTranslation();
  const { state, restoreItem, permanentDeleteItem, emptyTrash } = useFileManager();
  const { trash } = state;

  const [confirmDelete, setConfirmDelete] = useState<{ type: 'folder' | 'document'; id: number; name: string } | null>(null);
  const [confirmEmptyTrash, setConfirmEmptyTrash] = useState(false);

  if (!trash || trash.items.length === 0) {
    return (
      <EmptyState
        icon={<DeleteSweepIcon sx={{ fontSize: 80, color: 'grey.400' }} />}
        title={t('fileManager.trash.empty', 'La corbeille est vide')}
        description={t('fileManager.trash.emptyDescription', 'Les elements supprimes apparaitront ici')}
      />
    );
  }

  const handleRestore = async (type: 'folder' | 'document', id: number) => {
    await restoreItem(type, id);
  };

  const handlePermanentDelete = async () => {
    if (confirmDelete) {
      await permanentDeleteItem(confirmDelete.type, confirmDelete.id);
      setConfirmDelete(null);
    }
  };

  const handleEmptyTrash = async () => {
    await emptyTrash();
    setConfirmEmptyTrash(false);
  };

  return (
    <Box>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            {t('fileManager.trash.title', 'Corbeille')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('fileManager.trash.itemCount', '{{count}} element(s)', { count: trash.total_count })} -
            {' '}{formatFileSize(trash.total_size)}
          </Typography>
        </Box>

        <Button
          variant="outlined"
          color="error"
          startIcon={<DeleteForeverIcon />}
          onClick={() => setConfirmEmptyTrash(true)}
          disabled={trash.items.length === 0}
        >
          {t('fileManager.trash.emptyTrash', 'Vider la corbeille')}
        </Button>
      </Box>

      {/* Warning */}
      <Alert
        severity="warning"
        icon={<WarningAmberIcon />}
        sx={{ mb: 3, borderRadius: 2 }}
      >
        {t('fileManager.trash.warning', 'Les elements dans la corbeille seront automatiquement supprimes apres {{days}} jours.', {
          days: trash.days_until_auto_delete,
        })}
      </Alert>

      {/* Items table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, boxShadow: 'none', border: '1px solid', borderColor: 'grey.200' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>{t('fileManager.trash.name', 'Nom')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('fileManager.trash.type', 'Type')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('fileManager.trash.deletedAt', 'Supprime le')}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{t('fileManager.trash.daysRemaining', 'Jours restants')}</TableCell>
              <TableCell align="right" sx={{ fontWeight: 600 }}>{t('fileManager.trash.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {trash.items.map((item) => (
              <TableRow key={`${item.type}-${item.id}`} hover>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    {item.type === 'folder' ? (
                      <FolderIcon sx={{ color: '#2563eb' }} />
                    ) : (
                      <InsertDriveFileIcon sx={{ color: 'grey.500' }} />
                    )}
                    <Typography variant="body2" fontWeight={500}>
                      {item.name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={item.type === 'folder' ? t('fileManager.trash.folder', 'Dossier') : t('fileManager.trash.file', 'Fichier')}
                    size="small"
                    sx={{
                      bgcolor: item.type === 'folder' ? alpha('#2563eb', 0.1) : alpha('#64748b', 0.1),
                      color: item.type === 'folder' ? '#2563eb' : '#64748b',
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">
                    {new Date(item.deleted_at).toLocaleDateString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={`${item.days_remaining} ${t('fileManager.trash.days', 'jours')}`}
                    size="small"
                    color={item.days_remaining <= 7 ? 'error' : item.days_remaining <= 14 ? 'warning' : 'default'}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={t('fileManager.trash.restore', 'Restaurer')}>
                    <IconButton
                      size="small"
                      onClick={() => handleRestore(item.type, item.id)}
                      sx={{ color: 'success.main' }}
                    >
                      <RestoreIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t('fileManager.trash.deletePermanently', 'Supprimer definitivement')}>
                    <IconButton
                      size="small"
                      onClick={() => setConfirmDelete({ type: item.type, id: item.id, name: item.name })}
                      sx={{ color: 'error.main' }}
                    >
                      <DeleteForeverIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Confirm permanent delete dialog */}
      <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteForeverIcon color="error" />
          {t('fileManager.trash.confirmDeleteTitle', 'Supprimer definitivement')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('fileManager.trash.confirmDeleteMessage', 'Etes-vous sur de vouloir supprimer definitivement "{{name}}" ? Cette action est irreversible.', {
              name: confirmDelete?.name,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button onClick={handlePermanentDelete} color="error" variant="contained">
            {t('fileManager.trash.delete', 'Supprimer')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm empty trash dialog */}
      <Dialog open={confirmEmptyTrash} onClose={() => setConfirmEmptyTrash(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteForeverIcon color="error" />
          {t('fileManager.trash.confirmEmptyTitle', 'Vider la corbeille')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('fileManager.trash.confirmEmptyMessage', 'Etes-vous sur de vouloir vider la corbeille ? Tous les elements ({{count}}) seront supprimes definitivement.', {
              count: trash.total_count,
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEmptyTrash(false)}>
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button onClick={handleEmptyTrash} color="error" variant="contained">
            {t('fileManager.trash.emptyTrash', 'Vider la corbeille')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrashView;
